import * as RadixAvatar from '@radix-ui/react-avatar'
import classNames from 'classnames'
import React, { type CSSProperties, memo, useMemo } from 'react'
import { Spacing } from '@opallabs/gem'
import { extractInitials } from '@opallabs/lambda'
import { Tooltip } from '../Tooltip'
import './styles.css'
import { cssVariables, parts, selectors } from './styleHooks'
import { type AvatarProps, propDefaults } from './types'

export { type AvatarProps, DEFAULT_SIZE } from './types'
export { parts as avatarParts, root as avatarRoot } from './styleHooks'

const AvatarComponent = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      exportparts,
      fullName,
      initialsLength = propDefaults.initialsLength,
      locale,
      mode = propDefaults.mode,
      part,
      size = propDefaults.size,
      src,
      tooltipped = propDefaults.tooltipped,
      customTooltipText,
      ...otherProps
    },
    ref
  ) => {
    const initials = useMemo(
      () =>
        mode === 'truncate'
          ? fullName.substring(0, initialsLength)
          : extractInitials(fullName, {
              locale,
              maximumInitials: initialsLength,
            }),
      [fullName, locale, initialsLength, mode]
    )

    const content = (
      <RadixAvatar.Root
        {...otherProps}
        className={classNames(selectors.root, className)}
        data-tooltipped={tooltipped}
        data-unit={selectors.root}
        part={part}
        ref={ref}
        style={
          {
            [cssVariables.size]: `${size}px`,
          } as CSSProperties
        }
      >
        {/*
         * <jordan@> 2026-05-06 It would be more performant to do lazy-loading, but `<RadixAvatar.Image>`'s
         * implementation currently can't handle it. (The `<img>` `loading` prop is accepted, but does nothing.)
         *
         * @see https://github.com/mui/base-ui/issues/2597
         */}
        <RadixAvatar.Image
          alt={fullName}
          part={`${parts.image} ${exportparts?.image ?? ''}`}
          src={src}
        />
        <RadixAvatar.Fallback
          delayMs={src ? 1000 : 0}
          part={`${parts.text} ${exportparts?.text ?? ''}`}
        >
          {initials}
        </RadixAvatar.Fallback>
      </RadixAvatar.Root>
    )

    if (tooltipped) {
      return (
        <Tooltip
          content={customTooltipText ?? fullName}
          side="top"
          sideOffset={Spacing.XS}
          zIndex={9999}
        >
          {content}
        </Tooltip>
      )
    }

    return content
  }
)
AvatarComponent.displayName = 'Lapidary: Avatar'

export const Avatar = memo(AvatarComponent)
